"use client";

import type { HTMLAttributes, MouseEvent, ReactNode, FC, Ref } from "react";
import { isString, mergeClasses } from "@huub-web/utils";

import type { EdgesPropsWithOutClassNames } from "../../props";
import {
  cardStyle,
  innerCardStyle,
  growStyle,
  headerStyle,
  cardTitleStyle,
  cardSubtitleStyle,
} from "./BasicCard.css";
import { Card, type CardProps } from "../Card/Card";
import { Text } from "../Text/Text";
import { Flex, type FlexProps } from "../Flex/Flex";
import { Indicator, type IndicatorProps } from "../Indicator/Indicator";
import { titleStyle } from "../ExpansionPanel/ExpansionPanel.css";
import { Spacing } from "../Spacing";

export type BasicCardProps = Omit<
  HTMLAttributes<HTMLDivElement | HTMLButtonElement>,
  "title"
> &
  EdgesPropsWithOutClassNames &
  CardProps & {
    icon?: IndicatorProps["icon"];
    subtitle?: ReactNode;
    title?: ReactNode;
    indicatorVariant?: IndicatorProps["variant"];
    indicatorType?: IndicatorProps["type"];
    headerDirection?: "horizontal" | "vertical";
    justify?: FlexProps["justify"];
    innerClassName?: string;
    innerContentClassName?: string;
    headerClassName?: string;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement | HTMLButtonElement>;
  };

/**
 * BasicCard is a simple card component that can be used to display information.
 */
export const BasicCard: FC<BasicCardProps> = ({
  icon,
  subtitle,
  title,
  trailing,
  gap = "s",
  variant = "primary",
  indicatorVariant = "accent",
  indicatorType = "square",
  headerDirection = "horizontal",
  justify,
  onClick,
  border,
  elevated,
  className,
  innerClassName,
  innerContentClassName,
  headerClassName,
  children,
  ref,
  ...props
}) => {
  const isInteractive = !!onClick;

  const handleClick = (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    if (onClick) {
      onClick?.(e); // TODO: Tracking
    }
  };

  return (
    <Card
      ref={ref as Ref<HTMLDivElement>}
      as={isInteractive ? "button" : "div"}
      className={mergeClasses(cardStyle(), className)}
      innerClassName={mergeClasses(innerCardStyle({}), innerClassName)}
      elevated={elevated}
      variant={variant}
      onClick={isInteractive ? handleClick : undefined}
      gap={gap}
      border={elevated && !border ? "default" : border}
      {...props}
    >
      {(!!icon || title) && (
        <Flex
          direction={headerDirection === "horizontal" ? "row" : "column"}
          gap={headerDirection === "horizontal" ? "s" : "xs"}
          align="flex-start"
          className={mergeClasses(
            headerStyle({ direction: headerDirection }),
            headerClassName,
          )}
        >
          {!!icon && (
            <Indicator
              icon={icon}
              variant={indicatorVariant}
              type={indicatorType}
              size="inline"
            />
          )}
          {title && (
            <Flex direction="column" gap="xxs">
              <Text
                as="h3"
                className={mergeClasses(
                  titleStyle({ headerDirection }),
                  cardTitleStyle(),
                )}
                bold={headerDirection === "horizontal"}
              >
                {title}
              </Text>
              {isString(subtitle) && headerDirection === "horizontal" ? (
                <Text
                  variant="surfaceSubtitle"
                  color={variant === "primary" ? "currentColor50" : "secondary"}
                  className={cardSubtitleStyle()}
                >
                  {subtitle}
                </Text>
              ) : (
                subtitle
              )}
            </Flex>
          )}
        </Flex>
      )}

      {(children || (subtitle && headerDirection !== "horizontal")) && (
        <Flex
          className={mergeClasses(growStyle({ gap }), innerContentClassName)}
          direction="column"
          justify={justify}
        >
          {headerDirection !== "horizontal" &&
            (isString(subtitle) ? (
              <Text
                variant="surfaceSubtitle"
                color={variant === "primary" ? "onSecondary" : "onSecondary"}
              >
                {subtitle}
              </Text>
            ) : (
              subtitle
            ))}

          {isString(children) && (
            <Text variant="bodyM" color="currentColor50">
              {children}
            </Text>
          )}

          {!isString(children) && (
            <>
              <Spacing />
              {children}
            </>
          )}
        </Flex>
      )}

      {trailing && <div>{trailing}</div>}
    </Card>
  );
};
